/**
 * MediaGallery — thumbnail grid + lightbox over /api/job-media.
 *
 * The media source for every work surface: JobDetail, the Client-360 (whole
 * relationship), the FlowView drill-in, and request workspaces. Serves OUR
 * re-hosted copies (private `job-media` bucket, signed URLs) — never Jobber's
 * short-lived S3 links, which is why the old live photo strips kept decaying
 * to broken images.
 *
 *   <MediaGallery scope={{ job: 2648 }} />
 *   <MediaGallery scope={{ client: gid }} showContext />
 *   <MediaGallery scope={{ request: gid }} />
 *
 * scope: exactly one of { job, request, quote, invoice, client }.
 * showContext: caption thumbnails with the job/request they belong to (useful
 * when the grid spans a whole client relationship).
 *
 * House rules: inline styles only; edits, never rewrites.
 */
import { useState, useEffect, useMemo } from "react";
import { apiFetch } from "../lib/apiFetch";

const C = {
  surface: "#161a20", card: "#1c2028", border: "#2a3140",
  lime: "#a5ce3a", text: "#e8eaf0", muted: "#8892a4", faint: "#5a6472",
};

const fmtDate = (s) => (s ? new Date(s).toLocaleDateString("en-CA", { month: "short", day: "numeric", year: "numeric" }) : null);

function contextLabel(m) {
  if (m.jobNumber) return `Job #${m.jobNumber}`;
  if (m.requestTitle) return `Request — ${m.requestTitle}`;
  if (m.quoteNumber) return `Quote #${m.quoteNumber}`;
  if (m.invoiceNumber) return `Invoice #${m.invoiceNumber}`;
  return "Client note";
}

export function Lightbox({ items, index, onClose, onStep, showContext }) {
  const m = items[index];

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowRight") onStep(1);
      else if (e.key === "ArrowLeft") onStep(-1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, onStep]);

  if (!m) return null;
  const arrow = (dir) => ({
    position: "absolute", top: "50%", [dir > 0 ? "right" : "left"]: 10, transform: "translateY(-50%)",
    background: "rgba(0,0,0,.55)", border: `1px solid ${C.border}`, color: C.text, borderRadius: 10,
    fontSize: 26, lineHeight: 1, padding: "10px 14px", cursor: "pointer", userSelect: "none", zIndex: 2,
  });

  return (
    <div onClick={onClose}
      style={{ position: "fixed", inset: 0, background: "rgba(6,8,10,.92)", zIndex: 9000, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: 16 }}>
      <button onClick={onClose} aria-label="Close"
        style={{ position: "absolute", top: 12, right: 14, background: "rgba(0,0,0,.55)", border: `1px solid ${C.border}`, color: C.text, borderRadius: 10, fontSize: 20, padding: "6px 13px", cursor: "pointer", zIndex: 2 }}>×</button>
      {items.length > 1 && <button style={arrow(-1)} onClick={(e) => { e.stopPropagation(); onStep(-1); }} aria-label="Previous">‹</button>}
      {items.length > 1 && <button style={arrow(1)} onClick={(e) => { e.stopPropagation(); onStep(1); }} aria-label="Next">›</button>}

      <div onClick={(e) => e.stopPropagation()} style={{ maxWidth: "94vw", maxHeight: "80vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
        {m.isVideo ? (
          <video key={m.id} src={m.url} controls autoPlay style={{ maxWidth: "94vw", maxHeight: "78vh", borderRadius: 10, background: "#000" }} />
        ) : (
          <img key={m.id} src={m.url} alt={m.fileName || "photo"} style={{ maxWidth: "94vw", maxHeight: "78vh", objectFit: "contain", borderRadius: 10 }} />
        )}
      </div>

      <div onClick={(e) => e.stopPropagation()}
        style={{ marginTop: 10, maxWidth: 720, width: "100%", background: "rgba(16,20,26,.92)", border: `1px solid ${C.border}`, borderRadius: 10, padding: "9px 13px", fontSize: 12.5, color: C.muted }}>
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "baseline" }}>
          <span style={{ color: C.text, fontWeight: 700 }}>{index + 1}/{items.length}</span>
          {showContext && <span style={{ color: C.lime, fontWeight: 700 }}>{contextLabel(m)}</span>}
          {m.takenAt && <span>{fmtDate(m.takenAt)}</span>}
          {m.noteAuthor && <span>by {m.noteAuthor}</span>}
          <a href={m.url} target="_blank" rel="noreferrer" style={{ color: "#7dd3fc", textDecoration: "none", marginLeft: "auto" }}>open original ↗</a>
        </div>
        {m.noteMessage && (
          <div style={{ marginTop: 5, color: C.text, fontSize: 12.5, lineHeight: 1.45, maxHeight: 72, overflow: "hidden", whiteSpace: "pre-wrap" }}>{m.noteMessage}</div>
        )}
      </div>
    </div>
  );
}

export default function MediaGallery({ scope, showContext = false, initial = 24, thumbSize = 84, emptyText = null }) {
  const [items, setItems] = useState(null); // null = loading
  const [error, setError] = useState(null);
  const [showAll, setShowAll] = useState(false);
  const [lightbox, setLightbox] = useState(null); // index into viewable
  const scopeKey = JSON.stringify(scope || {});

  useEffect(() => {
    let dead = false;
    setItems(null); setError(null); setShowAll(false); setLightbox(null);
    (async () => {
      try {
        const r = await apiFetch("/api/job-media", {
          method: "POST", headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ action: "list", ...JSON.parse(scopeKey) }),
        });
        const j = await r.json();
        if (dead) return;
        if (!r.ok || j.ok === false) throw new Error(j.error || `HTTP ${r.status}`);
        setItems(j.media || []);
      } catch (e) { if (!dead) { setError(e.message); setItems([]); } }
    })();
    return () => { dead = true; };
  }, [scopeKey]);

  // Lightbox walks images+videos; docs open directly.
  const viewable = useMemo(() => (items || []).filter((m) => m.isImage || m.isVideo), [items]);
  const docs = useMemo(() => (items || []).filter((m) => !m.isImage && !m.isVideo), [items]);

  if (items === null) return <div style={{ color: C.faint, fontSize: 12.5, padding: "4px 0" }}>Loading photos…</div>;
  if (error) return null; // media is enrichment — never break the page over it
  if (!items.length) return emptyText ? <div style={{ color: C.faint, fontSize: 12.5, padding: "4px 0" }}>{emptyText}</div> : null;

  const shown = showAll ? viewable : viewable.slice(0, initial);
  const tile = { width: thumbSize, height: thumbSize, objectFit: "cover", borderRadius: 8, border: `1px solid ${C.border}`, display: "block", cursor: "pointer", background: C.card };

  return (
    <div>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        {shown.map((m, i) => (
          <div key={m.id} onClick={() => setLightbox(i)} title={`${showContext ? contextLabel(m) + " · " : ""}${m.fileName || ""}`} style={{ position: "relative" }}>
            {m.isVideo ? (
              <div style={{ ...tile, display: "flex", alignItems: "center", justifyContent: "center", color: C.text, fontSize: 24 }}>▶</div>
            ) : (
              <img src={m.thumbUrl || m.url} alt={m.fileName || "photo"} loading="lazy" style={tile} />
            )}
            {showContext && m.jobNumber && (
              <span style={{ position: "absolute", left: 4, bottom: 4, background: "rgba(6,8,10,.75)", color: C.lime, fontSize: 9.5, fontWeight: 700, borderRadius: 5, padding: "1px 5px" }}>
                #{m.jobNumber}
              </span>
            )}
          </div>
        ))}
        {!showAll && viewable.length > initial && (
          <button onClick={() => setShowAll(true)}
            style={{ ...tile, border: `1px dashed ${C.border}`, color: C.muted, fontSize: 12, fontWeight: 700, cursor: "pointer" }}>
            +{viewable.length - initial} more
          </button>
        )}
      </div>

      {docs.length > 0 && (
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 8 }}>
          {docs.map((m) => (
            <a key={m.id} href={m.url} target="_blank" rel="noreferrer"
              style={{ display: "inline-flex", alignItems: "center", gap: 6, background: C.card, border: `1px solid ${C.border}`, borderRadius: 8, padding: "5px 10px", fontSize: 12, color: C.text, textDecoration: "none", maxWidth: 240 }}>
              <span>📄</span>
              <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{m.fileName || "document"}</span>
            </a>
          ))}
        </div>
      )}

      {lightbox != null && (
        <Lightbox items={viewable} index={lightbox} showContext={showContext}
          onClose={() => setLightbox(null)}
          onStep={(d) => setLightbox((i) => (i + d + viewable.length) % viewable.length)} />
      )}
    </div>
  );
}
